import { useEffect, useMemo, useState } from "react";
import { KMITL } from "@kmitl/shared";
import type { AcademicSnapshot, FallbackTier } from "@kmitl/shared";
import { useTheme } from "../hooks/useTheme";
import { PipelinePill, type AdapterHealth } from "./AdapterHealthPanel";
import { loadPublicSheetsUrl } from "./SheetsPanel";
import { GearIcon, SunIcon, HalfIcon, DiamondIcon } from "./Icons";

interface FeedHealth {
  label: string;
  tier: FallbackTier | "loading";
  ageMinutes: number;
}

interface Props {
  feeds: FeedHealth[];
  onOpenCatalog: () => void;
  catalogCount: number;
  viewMode: "2D" | "3D" | "3DS";
  onCycleViewMode: () => void;
  onOpenManual: () => void;
  onOpenSheets: () => void;
  onOpenStandards: () => void;
  onOpenAnalytics?: () => void;
  sheetsConfigured: boolean;
  academic: AcademicSnapshot | null;
  adapterHealth: AdapterHealth[];
  /** Active lens — owns the slack in the middle column of the bar. */
  lens?: { label: string; describe: string } | null;
  /** Exhibition attract loop — auto-cycles lenses for expo booth operation. */
  expoMode?: boolean;
  onToggleExpo?: () => void;
  /** CCTV drawer state — operator clicks the chip to expand the live tile row. */
  cctvOpen?: boolean;
  onToggleCctv?: () => void;
}

function formatDate(d: Date): string {
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }).toUpperCase();
}

/**
 * Feed-chip acronym expansions — surface what each three-letter label
 * means as a tooltip so first-time operators don't have to open the
 * Manual modal to decode the chips.
 */
const FEED_GLOSSARY: Record<string, string> = {
  NEWS: "News (Google + Bangkok Post, EN/TH/ZH)",
  CR:   "Citizen Reports (Traffy Fondue)",
  iTIC: "iTIC traffic events (Longdo)",
  BMA:  "Bangkok Metropolitan Administration POIs",
  CCTV: "Live CCTV (Longdo)",
  BUS:  "Campus shuttle vehicles",
  AQ:   "Air Quality (Open-Meteo + Air4Thai)",
  WX:   "Weather (Open-Meteo)",
  EX:   "Executive briefing (derived)",
  MK:   "Markets (FMP + FRED)",
};

/**
 * Term-time colour is driven by `data-tempo` in CSS, not by an inline style.
 * The chip used to carry `style={{ color: "var(--warn)" }}` and be coloured by
 * a global `[style*="var(--warn)"] { … !important }` rule — meaning the colour
 * depended on the exact TEXT of the inline style attribute. Editing that string
 * silently turned the chip grey.
 */

export function TopBar({ feeds, onOpenCatalog, catalogCount, viewMode, onCycleViewMode, onOpenManual, onOpenSheets, onOpenStandards, onOpenAnalytics = () => {}, sheetsConfigured, academic, adapterHealth, lens = null, expoMode = false, onToggleExpo, cctvOpen = false, onToggleCctv }: Props) {
  const [now, setNow] = useState(() => new Date());
  const [moreOpen, setMoreOpen] = useState(false);
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const { theme, toggle } = useTheme();
  // Feeds that need a human.
  //
  // `FallbackTier` has six values, and only two of them are something an
  // operator must act on:
  //   unavailable — the feed is broken. Always named.
  //   scenario    — synthetic data. This project's rule is that modelled
  //                 numbers are labelled as modelled, so it stays visible
  //                 rather than passing as a measurement.
  //
  // `cache`, `database` and `reference` are legitimate ways to serve real
  // data — a served-from-cache BMA pull is still BMA data. Treating them as
  // failures made a healthy dashboard render five orange chips, because those
  // tiers are the *expected* steady state for most of the 12-hour adapters.
  const unhealthy = feeds.filter(
    (f) => f.tier === "unavailable" || f.tier === "scenario",
  );
  // Scenario feeds are a single condition, not N separate ones — three synthetic
  // adapters used to render three identical `SCENARIO` chips, which read as
  // three different faults and said the same thing three times. They collapse
  // into one counted chip whose tooltip names every feed in it.
  const unavailable = unhealthy.filter((f) => f.tier === "unavailable");
  const scenario = unhealthy.filter((f) => f.tier === "scenario");
  // Feeds actually reporting something, live or served from a cache/DB.
  const reportingCount = feeds.filter(
    (f) => f.tier !== "unavailable" && f.tier !== "loading",
  ).length;
  // Public read-only sheet URL — surfaces the LIVE DATA topbar button for
  // anyone visiting the dashboard. Built from VITE_SHEETS_VIEW_URL or
  // derived from the admin-configured /edit URL.
  const publicSheetUrl = useMemo(() => loadPublicSheetsUrl(), [sheetsConfigured]);

  return (
    <header className="topbar">
      {/* Sponsor + identity bar */}
      <div className="brand">
        <a
          className="sponsor"
          href="https://slic-index.nonarkara.org"
          target="_blank"
          rel="noreferrer"
          aria-label="SLIC Index — Smart Liveable Cities"
        >
          <img src="/logos/slic.png" alt="SLIC" />
        </a>
        <a
          className="sponsor"
          href="https://www.depa.or.th"
          target="_blank"
          rel="noreferrer"
          aria-label="depa — Digital Economy Promotion Agency"
        >
          <img src="/logos/depa.jpg" alt="depa" />
        </a>
        <span className="brand-sep" aria-hidden />
        <div className="brand-stack">
          <strong>KMITL Control Tower</strong>
          {/* KCT-01 only. The full institute name ran ~70 characters across a
              32 px row and pushed every control right; it lives in <title>,
              the About page and the header's aria-label instead. */}
          <span className="brand-sub mono" title={`KCT-01 · ${KMITL.name.en} · Bangkok`}>
            KCT-01
          </span>
        </div>
      </div>

      {/* Feed health. Ten acronym chips in a 32 px row read as a wall of
          three-letter noise and told an operator nothing actionable. What
          earns the space is "is anything broken" — so only `unavailable` and
          `scenario` feeds are named, and a healthy set collapses to one count.
          Full per-feed detail is in the Sources panel (MORE).

          Three states, and the third matters: with an empty feed list there is
          nothing to certify, so we render nothing at all. "0/0 LIVE" would be
          a green all-clear for a dashboard that has not loaded its feeds. */}
      <div className="feeds">
        {feeds.length === 0 ? null : unhealthy.length === 0 ? (
          <span
            className="feed-chip feed-ok"
            title={`${reportingCount} of ${feeds.length} feeds reporting · no failures`}
          >
            <span className="dot live" />
            {reportingCount}/{feeds.length} FEEDS
          </span>
        ) : (
          <>
            {unavailable.map((f) => (
              <span
                key={f.label}
                className="feed-chip feed-unavailable"
                title={`${FEED_GLOSSARY[f.label] ?? f.label} · unavailable${f.ageMinutes > 0 ? ` · last good ${f.ageMinutes}m ago` : ""}`}
              >
                <span className={`dot ${f.tier}`} />
                {f.label}
                <span className="feed-chip__tier">DOWN</span>
              </span>
            ))}
            {scenario.length > 0 && (
              <span
                className="feed-chip feed-scenario"
                title={`Modelled data, not measured — ${scenario
                  .map((f) => FEED_GLOSSARY[f.label] ?? f.label)
                  .join(" · ")}`}
              >
                <span className="dot scenario" />
                {scenario.length} SCENARIO
              </span>
            )}
          </>
        )}
      </div>

      {/* Lens readout. The middle column is the only part of a 32px bar with
          room to spare, and the one fact an operator most needs is which of
          the eleven lenses they are looking at. Measured: with this column
          empty, ~965px of plate sat between the feed chips and the controls.

          The full lens description lives in the tooltip — repeating it inline
          produced "LENS / OPS Operations", which says "Operations" twice. */}
      <div className="context">
        {lens && (
          <span title={lens.describe}>
            <span className="context__label">Lens</span>
            <span className="context__sep" aria-hidden>
              /
            </span>
            <span className="context__lens">{lens.label}</span>
          </span>
        )}
      </div>

      <div className="topbar-right">
        <PipelinePill adapters={adapterHealth} />
        {publicSheetUrl && (
          <a
            href={publicSheetUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mono sheets-btn sheets-btn-live live-data-btn"
            aria-label="Open the live-data Google Sheet, read-only — public transparency view"
            title="Live data — read-only sheet, every adapter refreshed every 5 min · public link, anyone can open"
          >
            ◉ LIVE DATA
          </a>
        )}

        {/* ── More menu (SHEETS / ANALYTICS / SOURCES / DT / MANUAL / ABOUT) ──
           Consolidates six rarely-used buttons into one dropdown. Keeps the
           topbar lean while every action stays one click away. The dropdown
           lives outside the topbar flow so it doesn't get clipped. Click on
           a menu item fires onClick + sets a `data-close` flag to keep the
           menu from re-opening on focus races. */}
        <div className="topbar-more">
          <button
            className="mono topbar-more__btn"
            aria-haspopup="menu"
            aria-expanded={moreOpen}
            onClick={() => setMoreOpen((v) => !v)}
            title="More — sheets, analytics, sources, standards, manual, about"
          >
            MORE {moreOpen ? "▴" : "▾"}
          </button>
          {moreOpen && (
            <>
              {/* Backdrop to close the menu on outside click */}
              <div
                className="topbar-more__backdrop"
                onClick={() => setMoreOpen(false)}
                aria-hidden
              />
              <div className="topbar-more__menu" role="menu">
                <button
                  role="menuitem"
                  className={`mono ${sheetsConfigured ? "sheets-btn-live" : ""}`}
                  onClick={() => { onOpenSheets(); setMoreOpen(false); }}
                >
                  <GearIcon /> Sheets setup
                </button>
                <button
                  role="menuitem"
                  className="mono"
                  onClick={() => { onOpenAnalytics(); setMoreOpen(false); }}
                >
                  ▦ Analytics
                </button>
                <button
                  role="menuitem"
                  className="mono"
                  onClick={() => { onOpenCatalog(); setMoreOpen(false); }}
                >
                  ◫ Sources · {catalogCount}
                </button>
                <button
                  role="menuitem"
                  className="mono"
                  onClick={() => { onOpenStandards(); setMoreOpen(false); }}
                >
                  ⌗ DT standards
                </button>
                <button
                  role="menuitem"
                  className="mono"
                  onClick={() => { onOpenManual(); setMoreOpen(false); }}
                >
                  ? Manual
                </button>
                <a
                  role="menuitem"
                  className="mono"
                  href="/about.html"
                  onClick={() => setMoreOpen(false)}
                >
                  ⓘ About
                </a>
              </div>
            </>
          )}
        </div>

        {onToggleExpo && (
          <button
            onClick={onToggleExpo}
            className={`mono expo-toggle${expoMode ? " active" : ""}`}
            aria-pressed={expoMode}
            aria-label={expoMode ? "Expo attract loop running — click to stop" : "Start expo attract loop — auto-cycles lenses every 45 s"}
            title={expoMode ? "EXPO loop ON — lenses auto-cycle every 45 s. Any touch pauses; resumes after 60 s idle." : "Exhibition mode — auto-cycle all lenses for booth display"}
          >
            EXPO
          </button>
        )}
        {onToggleCctv && (
          <button
            onClick={onToggleCctv}
            className={`mono cctv-toggle${cctvOpen ? " active" : ""}`}
            aria-pressed={cctvOpen}
            aria-label={cctvOpen ? "Hide live CCTV strip" : "Show live CCTV strip — 6 regional approach-road cameras"}
            title={cctvOpen ? "CCTV strip ON · click to close" : "CCTV strip OFF · click to show 6 regional cameras"}
          >
            ◉ CCTV
          </button>
        )}
        <button
          onClick={onCycleViewMode}
          className={`mono dim-toggle vm-${viewMode}`}
          aria-label={`View mode: ${viewMode}. Click to cycle 2D → 3D → 3DS X-RAY underground`}
          title={
            viewMode === "2D"
              ? "Tap → 3D · extrude buildings"
              : viewMode === "3D"
                ? "Tap → 3DS X-RAY · ghost buildings + show MEA/MWA/drainage/rail underground"
                : "Tap → 2D · top-down (hides underground layers)"
          }
        >
          {viewMode}
        </button>
        <button
          onClick={toggle}
          className="theme-toggle"
          aria-label={`Theme: ${theme}. Click to cycle dark → light → high-contrast`}
          title={theme === "dark" ? "Switch to light theme" : theme === "light" ? "Switch to high-contrast theme" : "Switch to dark theme"}
        >
          {theme === "dark" ? <SunIcon /> : theme === "light" ? <HalfIcon /> : <DiamondIcon />}
        </button>
      </div>

      <div className="clock">
        {academic?.current && (
          <span
            className="mono academic-chip"
            data-tempo={academic.tempo}
            title={`${academic.current.label} · ${academic.current.describe}${
              academic.next && academic.daysToNext != null
                ? ` · next: ${academic.next.label} in ${academic.daysToNext}d`
                : ""
            }`}
          >
            {academic.current.label.toUpperCase()}
          </span>
        )}
        {/* One time readout. The date used to sit beside the clock as a second
            styled element, which gave a 32 px row two competing right-hand
            anchors. The full date is in the tooltip; the clock is what an
            operator on a shift actually reads. */}
        <span
          className="clock-time"
          title={formatDate(now)}
        >
          {now.toLocaleTimeString("en-GB", { hour12: false })}
        </span>
      </div>
    </header>
  );
}
